<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, onUnmounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"
import { useQueryTags } from "@core/composables/useQueryTags"
import type { HomeBuildingNode } from "@modules/home/stores/home-list.types"
import { HOME_BUILDING_CARD_MIN_HEIGHT } from "@modules/home/components/dashboard/building/homeBuildingCardLayout"

defineOptions({
    name: "HomeBuildingList",
})

// ================================
// TYPES & INTERFACES
// ================================
type Variant = "root" | "search" | "folder"

interface Props {
    variant: Variant
    folderId?: string | null
    /** Masque le filtre tags (ex. recherche dossiers). */
    disableTags?: boolean
}

/** Options statiques de tri (liste bâtiments). */
const SORT_OPTIONS = [
    { label: "Date de création", value: "createdAt" },
    { label: "Nom", value: "name" },
    { label: "Adresse", value: "address" },
] as const

/** Options d’ordre asc/desc. */
const ORDER_OPTIONS = [
    { label: "Croissant", value: "asc" },
    { label: "Décroissant", value: "desc" },
] as const

/** Nombre de cartes squelette pendant un chargement initial / changement de dossier. */
const BUILDING_LIST_LOADING_SKELETON_COUNT = 6

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    folderId: undefined,
    disableTags: false,
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const homeWorkspace = useHomeWorkspaceStore()
const { selectedTags } = useQueryTags()

const {
    loadingRoot,
    loadingFolderBuildings,
    refreshingRoot,
    refreshingFolderBuildings,
    rootPagination,
    folderBuildingsPagination,
    rootBuildings,
    folderBuildings,
    companyTags,
} = storeToRefs(homeWorkspace)

// ================================
// STATE
// ================================
/** Élément sentinelle pour le chargement infini (intersection). */
const sentinel = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null

/**
 * Tant que le premier `load` du montage n’a pas fini, la liste est vide sans
 * `loading`/`refreshing` actifs — sans cela le message « Aucun bâtiment »
 * s’affiche un instant avant la requête.
 */
const suppressEmptyUntilFirstLoad = ref(true)

// ================================
// COMPUTED
// ================================
const getSortQueryKey = computed(() =>
    props.variant === "folder" ? "folderBuildingSort" : "sort"
)
const getOrderQueryKey = computed(() =>
    props.variant === "folder" ? "folderBuildingOrder" : "order"
)

const getSortValue = computed({
    get() {
        return (route.query[getSortQueryKey.value] as string) ?? "createdAt"
    },
    set(v: string) {
        router.replace({
            query: { ...route.query, [getSortQueryKey.value]: v },
        })
    },
})

const getOrderValue = computed({
    get() {
        const q = route.query[getOrderQueryKey.value] as string | undefined
        if (q) return q
        /** Racine : ordre asc par défaut ; dossier : date de création décroissante (cf. page dossiers). */
        return props.variant === "folder" ? "desc" : "asc"
    },
    set(v: string) {
        router.replace({
            query: { ...route.query, [getOrderQueryKey.value]: v },
        })
    },
})

const getRows = computed(() => {
    if (props.variant === "folder") {
        return folderBuildings.value
    }
    return rootBuildings.value
})

const getLoading = computed(() =>
    props.variant === "folder"
        ? loadingFolderBuildings.value
        : loadingRoot.value
)

/** Rechargement complet (tri, tags, recherche) — pas la pagination infinie. */
const getRefreshing = computed(() =>
    props.variant === "folder"
        ? refreshingFolderBuildings.value
        : refreshingRoot.value
)

const getHasMore = computed(() =>
    props.variant === "folder"
        ? folderBuildingsPagination.value.hasMore
        : rootPagination.value.hasMore
)

/**
 * Squelettes : premier rendu avant `onMounted`, refresh avec liste vidée, ou chargement
 * sans lignes encore. Pas en pagination infinie (lignes déjà présentes).
 */
const getShowBuildingListSkeleton = computed(
    () =>
        !getRows.value.length &&
        (suppressEmptyUntilFirstLoad.value ||
            getLoading.value ||
            getRefreshing.value)
)

/**
 * Texte à droite des filtres : compteur (dossier) ou chargement lors d’un refresh.
 */
const getBuildingListHeaderRight = computed(() => {
    if (getRefreshing.value) return "Chargement en cours..."
    if (props.variant !== "folder") return null
    const n = folderBuildingsPagination.value.total
    if (n === 0) return "Aucun bâtiment"
    if (n === 1) return "1 bâtiment"
    return `${n} bâtiments`
})

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Charge une page de bâtiments (racine ou dossier) selon le contexte.
 */
async function load(reset: boolean): Promise<void> {
    const sort = getSortValue.value
    const order = getOrderValue.value as "asc" | "desc"
    const tags = selectedTags.value

    if (props.variant === "folder") {
        await homeWorkspace.fetchFolderBuildingsPage({
            folderId: props.folderId ?? null,
            reset,
            tags,
            sort,
            order,
        })
    } else {
        const search = (route.query.search as string) || null
        await homeWorkspace.fetchRootBuildingsPage({
            reset,
            tags,
            search,
            sort,
            order,
        })
    }
}

/**
 * Configure l’observer d’intersection pour la pagination infinie.
 */
function setupObserver(): void {
    if (!sentinel.value) return
    observer?.disconnect()
    observer = new IntersectionObserver(
        (entries) => {
            const e = entries[0]
            if (e?.isIntersecting && getHasMore.value && !getLoading.value) {
                void load(false)
            }
        },
        { rootMargin: "120px" }
    )
    observer.observe(sentinel.value)
}

// ================================
// INTERACTIONS
// ================================
/**
 * Met à jour les tags sélectionnés et recharge la liste.
 */
function onTagsChange(v: string[]): void {
    selectedTags.value = v
    void load(true)
}

// ================================
// WATCHERS
// ================================
/**
 * Empreinte des paramètres qui impactent la liste bâtiments uniquement.
 * Important : une string (pas un tableau) pour que le watch ne se déclenche pas
 * quand d’autres query params changent (ex. `folderSort` / `folderOrder` dossiers)
 * tout en recalculant le computed — un tableau neuf à chaque fois ferait
 * toujours « changer » la valeur pour le watch.
 */
const getBuildingListWatchKey = computed(() => {
    const tagsKey = selectedTags.value.join(",")
    if (props.variant === "folder") {
        return [
            String(route.query.folderBuildingSort ?? ""),
            String(route.query.folderBuildingOrder ?? ""),
            tagsKey,
        ].join("|")
    }
    return [
        String(route.query.sort ?? ""),
        String(route.query.order ?? ""),
        String(route.query.search ?? ""),
        tagsKey,
    ].join("|")
})

watch(getBuildingListWatchKey, () => {
    void load(true)
})

watch(
    () => props.folderId,
    () => {
        if (props.variant === "folder") void load(true)
    }
)

watch(sentinel, () => setupObserver())

// ================================
// LIFECYCLE
// ================================
onMounted(async () => {
    homeWorkspace.registerBuildingsListContext(
        props.variant === "folder" ? "folder" : "root"
    )
    await homeWorkspace.loadCompanyTags()
    try {
        await load(true)
    } finally {
        suppressEmptyUntilFirstLoad.value = false
        setupObserver()
    }
})

onUnmounted(() => {
    observer?.disconnect()
})
</script>

<template>
    <!-- ================================ -->
    <!-- LISTE BÂTIMENTS — FILTRES + RÉSULTATS -->
    <!-- ================================ -->
    <section
        class="dashboard-building-list flex flex-col gap-4"
        data-cy="dashboard-building-list"
    >
        <div class="flex flex-wrap items-end justify-between gap-3">
            <div class="flex flex-wrap items-end gap-3">
                <div class="flex flex-col gap-1">
                    <label class="text-sm text-surface-600">Tri</label>
                    <BaseSelect
                        v-model="getSortValue"
                        :options="[...SORT_OPTIONS]"
                        option-label="label"
                        option-value="value"
                        class="min-w-40"
                    />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="text-sm text-surface-600">Ordre</label>
                    <BaseSelect
                        v-model="getOrderValue"
                        :options="[...ORDER_OPTIONS]"
                        option-label="label"
                        option-value="value"
                        class="min-w-36"
                    />
                </div>
                <div
                    v-if="!props.disableTags"
                    class="flex min-w-56 flex-col gap-1"
                >
                    <label class="text-sm text-surface-600">Étiquettes</label>
                    <BaseMultiSelect
                        :model-value="selectedTags"
                        :options="companyTags"
                        placeholder="Filtrer par étiquettes"
                        display="chip"
                        class="w-full"
                        @update:model-value="onTagsChange"
                    />
                </div>
            </div>
            <p
                v-if="getBuildingListHeaderRight != null"
                class="m-0 text-sm text-surface-600"
                aria-live="polite"
            >
                {{ getBuildingListHeaderRight }}
            </p>
        </div>

        <BaseMessage
            v-if="!getRows.length && !getShowBuildingListSkeleton"
            severity="info"
            :closable="false"
        >
            Aucun bâtiment à afficher.
        </BaseMessage>

        <div
            v-else-if="getShowBuildingListSkeleton"
            class="flex flex-col gap-4"
            aria-busy="true"
            aria-label="Chargement des bâtiments"
        >
            <BaseSkeleton
                v-for="n in BUILDING_LIST_LOADING_SKELETON_COUNT"
                :key="`building-skeleton-${n}`"
                class="w-full min-w-0 rounded-xl"
                :style="{ minHeight: HOME_BUILDING_CARD_MIN_HEIGHT }"
            />
        </div>

        <div v-else class="flex flex-col gap-4">
            <HomeBuildingRow
                v-for="row in getRows"
                :key="row._id_building ?? row._id"
                class="min-w-0"
                :building="row as HomeBuildingNode"
                :depth="0"
                :folder-list="props.variant === 'folder'"
                :folder-id="props.folderId ?? undefined"
            />
        </div>

        <div
            v-if="getLoading && !getRefreshing"
            class="text-center text-sm text-surface-500 py-4"
        >
            Chargement…
        </div>

        <div ref="sentinel" class="h-2 w-full" aria-hidden="true" />
    </section>
</template>
